import { Panel } from '../../platform/primitives/Panel'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { RECURRING_NEW_PATH, recurringDetailPath } from '../recurring/recurringPaths'
import { useBill } from '../recurring/useRecurring'
import type { Txn } from './transactionWire'

/** The row's one sentence before the bill's name is known, or if it never is. */
const PART_OF_FALLBACK = 'Part of a recurring payment'

/**
 * "Recurring" on the transaction detail (M7-R17, S14): the bill this payment
 * already settles, or the way to make one. M7 is reached only through its
 * exported hook and path builders, never its internals.
 */
export function RecurringRow({ txn }: { txn: Txn }) {
  return (
    <Panel className="txn-recurring">
      <h3 className="txn-recurring__label">Recurring</h3>
      <div className="txn-recurring__value">
        {txn.recurringStreamId !== null ? (
          <PartOf id={txn.recurringStreamId} />
        ) : (
          <InlineLink to={`${RECURRING_NEW_PATH}?from=${encodeURIComponent(txn.id)}`}>Set up recurring</InlineLink>
        )}
      </div>
    </Panel>
  )
}

/** Its own component so the bill is fetched only for a transaction that has one. */
function PartOf({ id }: { id: string }) {
  const bill = useBill(id)
  // A bill removed since the transaction was linked has no page to open.
  if (bill.removed) return <span>Part of a recurring payment that was removed</span>
  // Until the name arrives (or if it never does) the row still names what it is and still links.
  const name = bill.status === 'success' || bill.status === 'stale' ? bill.data.name : null
  return <InlineLink to={recurringDetailPath(id)}>{name === null ? PART_OF_FALLBACK : `Part of ${name}`}</InlineLink>
}
